/**
 * FlagshipPracticeV2 — Interactive Practice scenes for 5 more experiments.
 *   Boyle's Law, Prism (min-deviation), Photosynthesis, Osmosis, Chromatography.
 * All are tap/slider based (no external drag libs).
 */
import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Panel, Feedback, NextBar, Shell } from "./PracticeScaffold";

// (Panel/Feedback/NextBar/Shell now live in ./PracticeScaffold — was duplicated here until iter-26)

// ============================================================================
// BOYLE — piston compressing a gas column
// ============================================================================
export const BoylePractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [V, setV] = useState(60);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const P = 720 / V; // k = 720 → PV = 720 always
  const step = steps[stepIdx];

  const check = () => {
    const id = step.id;
    if (id === "p1" && V >= 55 && V <= 65) { setStatus((s) => ({ ...s, p1: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p1") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Aim closer to 60 cm³." });
    else if (id === "p2" && V >= 25 && V <= 35) { setStatus((s) => ({ ...s, p2: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p2") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Compress further." });
    else if (id === "p3" && V >= 40 && V <= 50) { setStatus((s) => ({ ...s, p3: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Aim around 45 cm³." });
    else if (id === "p4") { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };

  const reset = () => { setV(60); setStatus({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const scene = (
    <svg viewBox="0 0 400 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Cylinder body */}
      <rect x="130" y="60" width="140" height="380" fill="#0f172a" stroke="#475569" strokeWidth="3" rx="6" />
      {/* Trapped gas column — height maps to V */}
      <rect x="135" y={60 + (100 - V) * 3.4} width="130" height={V * 3.4} fill="url(#gasGrad)" opacity="0.7" />
      <defs>
        <linearGradient id="gasGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#0e7490" />
        </linearGradient>
      </defs>
      {/* Piston */}
      <g transform={`translate(0, ${60 + (100 - V) * 3.4})`}>
        <rect x="120" y="-14" width="160" height="20" fill="#94a3b8" stroke="#475569" strokeWidth="2" rx="3" />
        <line x1="200" y1="-14" x2="200" y2="-60" stroke="#94a3b8" strokeWidth="6" />
        <circle cx="200" cy="-70" r="14" fill="#f59e0b" />
      </g>
      {/* Pressure gauge */}
      <g transform="translate(320, 250)">
        <circle cx="0" cy="0" r="40" fill="#0f172a" stroke="#22c55e" strokeWidth="2" />
        <text x="0" y="-8" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">P</text>
        <text x="0" y="10" fill="#fde047" fontSize="14" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{P.toFixed(1)}</text>
        <text x="0" y="24" fill="#94a3b8" fontSize="8" textAnchor="middle">cm Hg</text>
      </g>
      {/* Volume label */}
      <text x="200" y="470" fill="#22d3ee" fontSize="14" textAnchor="middle" fontFamily="monospace">{`V = ${V.toFixed(0)} cm³`}</text>
      <text x="200" y="490" fill="#94a3b8" fontSize="10" textAnchor="middle" fontFamily="monospace">{`P × V = ${(P * V).toFixed(0)}`}</text>
    </svg>
  );

  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Piston position (Volume)</div>
        <Slider min={20} max={80} step={1} value={[V]} onValueChange={(v) => setV(v[0])} data-testid="boyle-piston" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>20 cm³</span><span className="text-cyan-300">V = {V} cm³</span><span>80 cm³</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="boyle-check">Check this step</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// PRISM — incident ray + emergent ray with deviation
// ============================================================================
export const PrismPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [i, setI] = useState(30);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [readings, setReadings] = useState({}); // {i30: D, i45:D, i60:D}
  const step = steps[stepIdx];

  // Deviation D(i) — smooth parabola with minimum around i=48°, Dmin≈38°
  const D = useMemo(() => {
    const iRad = (i - 48) / 30;
    return 38 + 22 * iRad * iRad; // ranges ~38 at min to ~54 at edges
  }, [i]);

  const check = () => {
    const id = step.id;
    if (id === "p1" && i >= 25 && i <= 35) { setStatus((s) => ({ ...s, p1: "ok" })); setReadings((r) => ({ ...r, i30: D })); setFb({ type: "ok", msg: `${step.correct_hint} D = ${D.toFixed(1)}°.` }); }
    else if (id === "p1") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Aim around 30°." });
    else if (id === "p2" && i >= 40 && i <= 50) { setStatus((s) => ({ ...s, p2: "ok" })); setReadings((r) => ({ ...r, i45: D })); setFb({ type: "ok", msg: `${step.correct_hint} D = ${D.toFixed(1)}°.` }); }
    else if (id === "p2") setFb({ type: "warn", msg: "Try i near 45°." });
    else if (id === "p3" && i >= 55 && i <= 65) { setStatus((s) => ({ ...s, p3: "ok" })); setReadings((r) => ({ ...r, i60: D })); setFb({ type: "ok", msg: `${step.correct_hint} D = ${D.toFixed(1)}°.` }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Try i near 60°." });
    else if (id === "p4") {
      const minD = Math.min(readings.i30 || 99, readings.i45 || 99, readings.i60 || 99);
      const bestI = readings.i45 === minD ? "45°" : readings.i30 === minD ? "30°" : "60°";
      setStatus((s) => ({ ...s, p4: "ok" }));
      setFb({ type: "ok", msg: `Minimum deviation found at i ≈ ${bestI} (D ≈ ${minD.toFixed(1)}°). ${step.correct_hint}` });
    }
  };
  const reset = () => { setI(30); setStatus({}); setFb(null); setReadings({}); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  // Ray geometry — approximate
  const cx = 240, cy = 250;
  const dr = i * Math.PI / 180;
  const inX = cx - 200 * Math.cos(dr);
  const inY = cy + 200 * Math.sin(dr);
  const outAngle = (i - D + 60) * Math.PI / 180;
  const outX = cx + 200 * Math.cos(outAngle);
  const outY = cy + 200 * Math.sin(outAngle);

  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Prism triangle */}
      <polygon points={`${cx},${cy - 90} ${cx - 70},${cy + 60} ${cx + 70},${cy + 60}`} fill="rgba(59, 130, 246, 0.28)" stroke="#38bdf8" strokeWidth="2" />
      {/* Incident ray */}
      <line x1={inX} y1={inY} x2={cx - 30} y2={cy + 15} stroke="#fde047" strokeWidth="3" markerEnd="url(#arr)" />
      {/* Emergent ray — split into 3 rainbow rays */}
      {[{ o: -6, c: "#f43f5e" }, { o: 0, c: "#22c55e" }, { o: 6, c: "#3b82f6" }].map((ray, k) => (
        <line
          key={k}
          x1={cx + 30}
          y1={cy + 15 + ray.o}
          x2={outX + 8 * (k - 1)}
          y2={outY + 8 * (k - 1)}
          stroke={ray.c}
          strokeWidth="2.5"
          opacity="0.9"
        />
      ))}
      {/* Extended incident direction (dashed) for deviation angle */}
      <line x1={inX} y1={inY} x2={cx + 200} y2={cy + 15 + (200 * Math.tan(dr))} stroke="#64748b" strokeWidth="1" strokeDasharray="4 3" />
      <defs>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><polygon points="0 0, 6 3, 0 6" fill="#fde047" /></marker>
      </defs>
      {/* Labels */}
      <text x="30" y="30" fill="#22d3ee" fontSize="14" fontFamily="monospace">{`i = ${i}°`}</text>
      <text x="30" y="52" fill="#f59e0b" fontSize="14" fontFamily="monospace">{`D = ${D.toFixed(1)}°`}</text>
      {readings.i30 !== undefined && <text x="30" y="440" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`D₃₀ = ${readings.i30.toFixed(1)}°`}</text>}
      {readings.i45 !== undefined && <text x="30" y="458" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`D₄₅ = ${readings.i45.toFixed(1)}°`}</text>}
      {readings.i60 !== undefined && <text x="30" y="476" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`D₆₀ = ${readings.i60.toFixed(1)}°`}</text>}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Angle of incidence i</div>
        <Slider min={20} max={70} step={1} value={[i]} onValueChange={(v) => setI(v[0])} data-testid="prism-incident" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>20°</span><span className="text-cyan-300">i = {i}°</span><span>70°</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="prism-check">Record this reading</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// PHOTOSYNTHESIS — lamp distance vs bubble rate
// ============================================================================
export const PhotoPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [d, setD] = useState(50);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [readings, setReadings] = useState({});
  const step = steps[stepIdx];

  // bubbles per minute: saturates at ~120, roughly 6000 / d² near normal range
  const bpm = useMemo(() => Math.min(140, Math.round(4500 / (d * d) * 60)), [d]);
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhase((p) => (p + 1) % 60), 80);
    return () => clearInterval(t);
  }, []);

  const check = () => {
    const id = step.id;
    if (id === "p1" && d >= 45 && d <= 60) { setStatus((s) => ({ ...s, p1: "ok" })); setReadings((r) => ({ ...r, d50: bpm })); setFb({ type: "ok", msg: `${step.correct_hint} Rate: ${bpm} bpm.` }); }
    else if (id === "p1") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Aim further — ~50 cm." });
    else if (id === "p2" && d >= 15 && d <= 25) { setStatus((s) => ({ ...s, p2: "ok" })); setReadings((r) => ({ ...r, d20: bpm })); setFb({ type: "ok", msg: `${step.correct_hint} Rate: ${bpm} bpm.` }); }
    else if (id === "p2") setFb({ type: "warn", msg: "Bring lamp closer, around 20 cm." });
    else if (id === "p3" && d >= 8 && d <= 14) { setStatus((s) => ({ ...s, p3: "ok" })); setReadings((r) => ({ ...r, d10: bpm })); setFb({ type: "ok", msg: `${step.correct_hint} Rate: ${bpm} bpm.` }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Even closer — ~10 cm." });
    else if (id === "p4") { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };
  const reset = () => { setD(50); setStatus({}); setFb(null); setReadings({}); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Lab bench */}
      <rect x="20" y="380" width="460" height="10" fill="#334155" />
      {/* Beaker with water */}
      <path d="M 320 200 L 320 380 L 440 380 L 440 200 Z" fill="rgba(34,211,238,0.15)" stroke="#0891b2" strokeWidth="2.5" />
      {/* Pondweed */}
      <g transform="translate(380, 340)">
        <line x1="0" y1="0" x2="0" y2="-100" stroke="#166534" strokeWidth="3" />
        {[15, 40, 65, 85].map((y, k) => (
          <g key={k} transform={`translate(0, -${y})`}>
            <ellipse cx={k % 2 ? 14 : -14} cy="0" rx="10" ry="4" fill="#16a34a" />
          </g>
        ))}
      </g>
      {/* Bubbles */}
      {[...Array(Math.max(2, Math.round(bpm / 12)))].map((_, k) => {
        const y = 320 - ((phase + k * 8) % 60) * 2;
        return <circle key={k} cx={380 + Math.sin(k * 1.5) * 8} cy={y} r={3} fill="#a5f3fc" opacity="0.85" />;
      })}
      {/* Lamp */}
      <g transform={`translate(${310 - d * 4}, 240)`}>
        <rect x="-30" y="-15" width="30" height="30" fill="#fde047" stroke="#eab308" strokeWidth="2" rx="4" />
        <line x1="0" y1="0" x2="20" y2="0" stroke="#fde047" strokeWidth="4" />
        <line x1="-30" y1="15" x2="-30" y2="60" stroke="#64748b" strokeWidth="3" />
        <rect x="-40" y="60" width="20" height="10" fill="#334155" />
      </g>
      {/* Distance line */}
      <line x1={310 - d * 4} y1="270" x2="320" y2="270" stroke="#22d3ee" strokeWidth="1" strokeDasharray="4 3" />
      <text x={(310 - d * 4 + 320) / 2} y="285" fill="#22d3ee" fontSize="11" textAnchor="middle" fontFamily="monospace">{`${d} cm`}</text>
      {/* Bubble counter */}
      <g transform="translate(60, 40)">
        <rect x="0" y="0" width="140" height="60" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
        <text x="70" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Bubbles / min</text>
        <text x="70" y="48" fill="#fde047" fontSize="22" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{bpm}</text>
      </g>
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Lamp distance from pondweed</div>
        <Slider min={5} max={70} step={1} value={[d]} onValueChange={(v) => setD(v[0])} data-testid="photo-lamp" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>5 cm</span><span className="text-cyan-300">d = {d} cm</span><span>70 cm</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="photo-check">Record this reading</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// OSMOSIS — five beakers, tap to drop strip
// ============================================================================
export const OsmosisPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [dropped, setDropped] = useState({}); // { beakerIdx: dm% }
  const step = steps[stepIdx];

  const BEAKERS = [
    { conc: 0, label: "0 M", dm: 12 },
    { conc: 0.2, label: "0.2 M", dm: 4 },
    { conc: 0.4, label: "0.4 M", dm: -1 },
    { conc: 0.6, label: "0.6 M", dm: -8 },
    { conc: 1.0, label: "1.0 M", dm: -18 },
  ];

  const drop = (idx) => {
    if (dropped[idx] !== undefined) return;
    setDropped((d) => ({ ...d, [idx]: BEAKERS[idx].dm }));
    const id = step.id;
    if (id === "p1" && idx === 0) { setStatus((s) => ({ ...s, p1: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p1") setFb({ type: "warn", msg: "Start with the pure-water beaker (leftmost)." });
    else if (id === "p2" && idx === 2) { setStatus((s) => ({ ...s, p2: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p2") setFb({ type: "warn", msg: "Drop into the 0.4 M beaker (middle)." });
    else if (id === "p3" && idx === 4) { setStatus((s) => ({ ...s, p3: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Drop into the 1.0 M beaker (rightmost)." });
  };
  const verify = () => { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); };
  const reset = () => { setDropped({}); setStatus({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      <rect x="20" y="400" width="560" height="10" fill="#334155" />
      {BEAKERS.map((b, k) => {
        const x = 40 + k * 108;
        const dm = dropped[k];
        return (
          <g key={k} onClick={() => drop(k)} style={{ cursor: dm === undefined ? "pointer" : "default" }} data-testid={`osmosis-beaker-${k}`}>
            <path d={`M ${x} 240 L ${x} 400 L ${x + 90} 400 L ${x + 90} 240 Z`} fill="rgba(34,211,238,0.15)" stroke="#0891b2" strokeWidth="2" />
            <text x={x + 45} y="230" fill="#22d3ee" fontSize="12" textAnchor="middle" fontFamily="monospace">{b.label}</text>
            {dm !== undefined && (
              <>
                <rect x={x + 32} y={340 - Math.abs(dm) * (dm > 0 ? 1.5 : -1.5)} width="26" height={40 + Math.abs(dm) * (dm > 0 ? 1.5 : -1.5)} fill={dm > 5 ? "#fde047" : dm < -5 ? "#78350f" : "#f59e0b"} rx="2" opacity="0.9" />
                <text x={x + 45} y="418" fill={dm > 0 ? "#22c55e" : dm < -5 ? "#ef4444" : "#eab308"} fontSize="11" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{dm > 0 ? "+" : ""}{dm}%</text>
              </>
            )}
            {dm === undefined && (
              <text x={x + 45} y="325" fill="#64748b" fontSize="10" textAnchor="middle">tap → drop</text>
            )}
          </g>
        );
      })}
      <text x="300" y="45" fill="#22d3ee" fontSize="14" textAnchor="middle" fontFamily="monospace">Potato strips in different sucrose concentrations</text>
      <text x="300" y="65" fill="#64748b" fontSize="10" textAnchor="middle">Bar shows % mass change after 30 min</text>
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      {step?.id === "p4" && (
        <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={verify} data-testid="osmosis-verify">Verify results</Button>
      )}
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// CHROMATOGRAPHY — spot ink, lower strip, wait, measure Rf
// ============================================================================
export const ChromatoPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [spot, setSpot] = useState(false);
  const [lowered, setLowered] = useState(false);
  const [waited, setWaited] = useState(false);
  const [measured, setMeasured] = useState(false);
  const step = steps[stepIdx];

  const act = () => {
    const id = step.id;
    if (id === "p1") { setSpot(true); setStatus((s) => ({ ...s, p1: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p2") { if (!spot) { setFb({ type: "warn", msg: "Place the ink spot first." }); return; } setLowered(true); setStatus((s) => ({ ...s, p2: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p3") { if (!lowered) { setFb({ type: "warn", msg: "Lower the strip into the solvent first." }); return; } setWaited(true); setStatus((s) => ({ ...s, p3: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p4") { if (!waited) { setFb({ type: "warn", msg: "Wait for separation to finish first." }); return; } setMeasured(true); setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };
  const reset = () => { setSpot(false); setLowered(false); setWaited(false); setMeasured(false); setStatus({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const stripY = lowered ? 220 : 90;
  const solventFront = waited ? 100 : lowered ? 380 : 0;

  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Beaker */}
      <path d="M 100 320 L 100 450 L 400 450 L 400 320 Z" fill="rgba(34,211,238,0.15)" stroke="#0891b2" strokeWidth="2.5" />
      {/* Solvent liquid */}
      <rect x="100" y="380" width="300" height="70" fill="rgba(59,130,246,0.35)" />
      {/* Filter paper strip */}
      <g transform={`translate(220, ${stripY})`}>
        <rect x="0" y="0" width="60" height="220" fill="#fefce8" stroke="#eab308" strokeWidth="1" />
        {/* Baseline */}
        <line x1="0" y1="200" x2="60" y2="200" stroke="#94a3b8" strokeDasharray="3 3" />
        <text x="65" y="204" fill="#94a3b8" fontSize="9" fontFamily="monospace">start</text>
        {/* Ink spot / bands */}
        {spot && !waited && (
          <circle cx="30" cy="200" r="8" fill="#1f2937" opacity="0.9" />
        )}
        {waited && (
          <>
            <circle cx="30" cy="200" r="6" fill="#dc2626" opacity="0.8" />
            <circle cx="30" cy="160" r="6" fill="#16a34a" opacity="0.8" />
            <circle cx="30" cy="120" r="6" fill="#2563eb" opacity="0.8" />
            <line x1="0" y1={solventFront} x2="60" y2={solventFront} stroke="#0891b2" strokeWidth="1.5" strokeDasharray="2 2" />
            <text x="65" y={solventFront + 4} fill="#22d3ee" fontSize="9" fontFamily="monospace">front</text>
          </>
        )}
      </g>
      {/* Support rod */}
      <line x1="250" y1="60" x2="250" y2={stripY} stroke="#64748b" strokeWidth="2" />
      <rect x="150" y="55" width="200" height="8" fill="#334155" rx="2" />
      {/* Rf display */}
      {measured && (
        <g transform="translate(60, 40)">
          <rect x="0" y="0" width="160" height="70" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
          <text x="80" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Blue band Rf</text>
          <text x="80" y="46" fill="#fde047" fontSize="18" textAnchor="middle" fontFamily="monospace" fontWeight="bold">0.80</text>
          <text x="80" y="62" fill="#94a3b8" fontSize="8" textAnchor="middle">80mm / 100mm</text>
        </g>
      )}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={act} data-testid="chromato-action">
        {step?.id === "p1" ? "Spot ink" : step?.id === "p2" ? "Lower into solvent" : step?.id === "p3" ? "Wait 5 min" : "Measure Rf"}
      </Button>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};
